import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FolderKanban,
  Receipt,
  UserCircle,
  Users,
  Globe,
} from 'lucide-react'
import { ApiError } from '../../../lib/api'
import { formatAppDateTime } from '@/lib/locale'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { KpiCard, KpiCardSkeleton } from '../../dashboard/components/KpiCard'
import { fetchBusiness } from '../api'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { BusinessDetailRecord, BusinessLifecycleActor } from '@/types/businesses'

/* ── Status badge ── */
function businessStatusBadgeClass(status: string) {
  switch (status) {
    case 'approved':
      return 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    case 'pending':
      return 'border-transparent bg-amber-500/10 text-amber-700 dark:text-amber-300'
    case 'cancelled':
      return 'border-transparent bg-rose-500/10 text-rose-700 dark:text-rose-300'
    case 'rejected':
      return 'border-transparent bg-rose-500/10 text-rose-700 dark:text-rose-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

/* ── Activity tones ── */
type ActivityTone = 'success' | 'active' | 'warning' | 'danger' | 'neutral'

const activityToneMap: Record<string, ActivityTone> = {
  created: 'success',
  invited: 'active',
  accepted: 'success',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'danger',
  suspended: 'warning',
  archived: 'neutral',
  synced: 'active',
}

function activityToneClass(tone: ActivityTone) {
  switch (tone) {
    case 'success':
      return 'border-transparent bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
    case 'active':
      return 'border-transparent bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
    case 'warning':
      return 'border-transparent bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
    case 'danger':
      return 'border-transparent bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

function activityStatusLabel(t: (k: string) => string, tone: ActivityTone) {
  switch (tone) {
    case 'success':
      return t('common.success')
    case 'active':
      return t('common.info')
    case 'warning':
      return t('common.warning')
    case 'danger':
      return t('common.error')
    default:
      return t('common.info')
  }
}

/* ── Activity events ── */
type ActivityKey =
  | 'created'
  | 'invited'
  | 'accepted'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'suspended'
  | 'archived'
  | 'synced'

interface ActivityEvent {
  key: ActivityKey
  timestamp: string
  actor?: BusinessLifecycleActor | null
}

function buildActivityEvents(business: BusinessDetailRecord): ActivityEvent[] {
  const events: ActivityEvent[] = []

  const push = (key: ActivityKey, timestamp: string | null, actor?: BusinessLifecycleActor | null) => {
    if (timestamp) events.push({ key, timestamp, actor })
  }

  push('synced', business.last_synced_at)
  push('archived', business.archived_at ?? null)
  push('suspended', business.suspended_at ?? null)
  push('cancelled', business.cancelled_at ?? null, business.cancelled_by)
  push('rejected', business.rejected_at, business.rejected_by)
  push('approved', business.approved_at, business.approved_by)
  push('accepted', business.accepted_at)
  push('invited', business.invited_at)
  push('created', business.created_at)

  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

/* ── Skeleton ── */
function BusinessDetailSkeleton() {
  return (
    <section className="app-section space-y-6">
      <div className="rounded-xl bg-card p-5">
        <div className="flex items-start gap-4">
          <Skeleton className="size-12 shrink-0 rounded-md" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>

      <Skeleton className="h-64 w-full rounded-xl" />
    </section>
  )
}

/* ── Page ── */
export function BusinessDetailPage() {
  const { t } = useTranslation()
  const { businessId = '' } = useParams()

  const [activityPage, setActivityPage] = useState(1)
  const [activityPageSize, setActivityPageSize] = useState(10)

  const query = useQuery({
    queryKey: ['businesses', 'detail', businessId],
    queryFn: () => fetchBusiness(businessId),
    enabled: businessId.length > 0,
  })

  const business = query.data?.data

  useAppBreadcrumbTrail(
    business
      ? [
          { label: t('navigation.businesses'), to: '/businesses' },
          { label: business.display_name },
        ]
      : null,
  )

  const activityEvents = useMemo(() => {
    if (!business) return []
    return buildActivityEvents(business)
  }, [business])

  const totalActivityItems = activityEvents.length
  const totalActivityPages = Math.max(1, Math.ceil(totalActivityItems / activityPageSize))
  const activityPageSafe = Math.min(activityPage, totalActivityPages)

  const activitySlice = useMemo(() => {
    const start = (activityPageSafe - 1) * activityPageSize
    return activityEvents.slice(start, start + activityPageSize)
  }, [activityEvents, activityPageSafe, activityPageSize])

  if (query.isPending) {
    return <BusinessDetailSkeleton />
  }

  if (query.isError) {
    return (
      <article className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
        {(query.error as ApiError).message}
      </article>
    )
  }

  if (!business) {
    return (
      <article className="rounded-xl bg-card px-4 py-3 text-sm text-muted-foreground">
        {t('businesses.detail.unavailable')}
      </article>
    )
  }

  const metaParts = [
    business.industry ?? t('businesses.detail.industryNotSpecified'),
    business.country_code ?? t('businesses.detail.countryPending'),
    business.currency_code,
    business.timezone,
  ].filter(Boolean)

  return (
    <section className="app-section space-y-6">
      {/* Header — flat card */}
      <div className="rounded-xl bg-card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <Avatar className="size-12 shrink-0 rounded-md border border-border/40">
              <AvatarImage src={business.logo_url ?? undefined} alt={business.display_name} className="object-contain" />
              <AvatarFallback className="rounded-md text-sm font-semibold">
                {business.display_name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground">{business.display_name}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">{business.legal_name}</p>
              <p className="mt-1 text-xs text-muted-foreground">{metaParts.join(' · ')}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={businessStatusBadgeClass(business.status)}>
              {t(`businesses.status.${business.status}`, { defaultValue: business.status })}
            </Badge>
            {business.website_url && (
              <a
                href={business.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <Globe className="size-3" />
                {t('common.visit')}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          title={t('businesses.detail.kpis.programs')}
          value={String(business.summary.program_count)}
          description={t('businesses.detail.kpis.programsDescription')}
          icon={FolderKanban}
          tone="primary"
        />
        <KpiCard
          title={t('businesses.detail.kpis.activeAgents')}
          value={String(business.summary.active_agent_count)}
          description={t('businesses.detail.kpis.activeAgentsDescription')}
          icon={Users}
          tone="info"
        />
        <KpiCard
          title={t('businesses.detail.kpis.prospects')}
          value={String(business.summary.prospect_count)}
          description={t('businesses.detail.kpis.prospectsDescription')}
          icon={UserCircle}
          tone="warning"
        />
        <KpiCard
          title={t('businesses.detail.kpis.transactions')}
          value={String(business.summary.transaction_count)}
          description={t('businesses.detail.kpis.transactionsDescription')}
          icon={Receipt}
          tone="success"
        />
      </div>

      {/* Activity — table with colored badges + pagination */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-sm font-semibold text-foreground">{t('businesses.detail.activity.title')}</h3>
          <span className="text-xs text-muted-foreground">
            {t('businesses.detail.activity.count', { count: activityEvents.length })}
          </span>
        </div>

        {activityEvents.length === 0 ? (
          <div className="px-5 pb-4 text-sm text-muted-foreground">{t('businesses.detail.activity.empty')}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                      {t('businesses.detail.activity.table.date')}
                    </TableHead>
                    <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                      {t('businesses.detail.activity.table.action')}
                    </TableHead>
                    <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                      {t('businesses.detail.activity.table.detail')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activitySlice.map((event, index) => {
                    const tone = activityToneMap[event.key] ?? 'neutral'
                    const actionLabel = t(`businesses.detail.activity.actions.${event.key}`)
                    const statusLabel = activityStatusLabel(t, tone)
                    const detail = t(`businesses.detail.activity.details.${event.key}`, {
                      name: event.actor?.display_name ?? '',
                      defaultValue: '',
                    })

                    return (
                      <TableRow key={`${event.key}-${index}`} className="group">
                        <TableCell className="py-2.5 text-sm tabular-nums text-muted-foreground">
                          {formatAppDateTime(event.timestamp)}
                        </TableCell>
                        <TableCell className="py-2.5">
                          <div className="flex items-center gap-2.5">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
                                activityToneClass(tone),
                              )}
                            >
                              {statusLabel}
                            </span>
                            <span className="text-sm text-foreground">{actionLabel}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5 text-sm text-muted-foreground">
                          {detail}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            <TablePaginationBar
              page={activityPageSafe}
              pageSize={activityPageSize}
              totalItems={totalActivityItems}
              onPageChange={setActivityPage}
              onPageSizeChange={(size) => {
                setActivityPageSize(size)
                setActivityPage(1)
              }}
              pageSizeOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </div>

      {/* Owners + Programs grid */}
      <div className="grid gap-6 xl:grid-cols-2">
        {/* Owners */}
        <div className="rounded-xl bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('businesses.detail.owners.title')}</h3>

          <div className="mt-4 space-y-3">
            {business.owners.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('businesses.detail.noOwnerAssignments')}</p>
            ) : (
              business.owners.map((owner) => (
                <div
                  key={owner.assignment_id}
                  className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5"
                >
                  <Avatar className="size-8 shrink-0 rounded-md">
                    <AvatarFallback className="rounded-md text-[10px] font-semibold">
                      {(owner.user?.display_name ?? '??').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">
                        {owner.user?.display_name ?? t('businesses.detail.invitationPending')}
                      </p>
                      {owner.is_primary && (
                        <Badge variant="outline" className="h-5 border-transparent bg-primary/10 px-1.5 text-[10px] text-primary">
                          {t('businesses.detail.primaryOwner')}
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {owner.user?.email ?? t('businesses.detail.noEmailAvailable')}
                    </p>
                  </div>
                  <Badge variant="outline" className="h-5 border-transparent bg-muted px-1.5 text-[10px] text-muted-foreground capitalize">
                    {owner.status}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Programs — non-clickable preview */}
        <div className="rounded-xl bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('businesses.detail.programs.title')}</h3>

          <div className="mt-4 space-y-3">
            {business.linked_programs.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('businesses.detail.noProgramsAttached')}</p>
            ) : (
              business.linked_programs.map((program) => (
                <div
                  key={program.id}
                  className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5"
                >
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <FolderKanban className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">
                        {program.name}
                      </p>
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-5 border-transparent px-1.5 text-[10px] capitalize',
                          program.status === 'active'
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {program.status}
                      </Badge>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {program.commission_type} · {program.exchange_mode}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
